import { useState, useCallback, useEffect, useRef } from 'react';
import { getCurrentUser } from '../../profile/services';
import { formatEventDate, formatEventTime } from '../../utils/dateTimeUtils';
import * as eventsApi from '../services/eventsApi';

export const useEvents = (ownerType, ownerId, shouldLoad = true) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Enrich event with pre-formatted local timezone display properties
   * This allows UI components to use event.start_date_display, event.start_time_display, etc.
   * without calling any formatting functions - timezone conversion is done here once
   */
  const enrichEventWithDisplayProperties = (event) => {
    return {
      ...event,
      // Pre-formatted display properties (already converted from UTC to local timezone)
      start_date_display: formatEventDate(event.start_time),
      start_time_display: formatEventTime(event.start_time),
      end_date_display: formatEventDate(event.end_time),
      end_time_display: formatEventTime(event.end_time),
      // Computed display strings
      start_datetime_display: `${formatEventDate(event.start_time)} at ${formatEventTime(event.start_time)}`,
      end_datetime_display: `${formatEventDate(event.end_time)} at ${formatEventTime(event.end_time)}`,
    };
  };

  // The range the list is currently showing, so a refetch can reproduce it.
  // Without this a refresh would silently widen or narrow what is on screen.
  const lastFetchArgs = useRef([null, null, false]);

  // Readable synchronously, unlike `events` inside a useCallback with no deps.
  const eventsRef = useRef([]);
  eventsRef.current = events;

  const fetchEvents = useCallback(async (startDate, endDate, includePast = false) => {
    if (!ownerType || !ownerId) return;
    lastFetchArgs.current = [startDate, endDate, includePast];

    try {
      setLoading(true);
      setError(null);

      const eventsList = await eventsApi.fetchEvents(ownerType, ownerId, startDate, endDate, includePast);

      // Enrich with pre-formatted display properties (UTC → local timezone conversion)
      const transformedEvents = eventsList.map(enrichEventWithDisplayProperties);

      setEvents(transformedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [ownerType, ownerId]);

  const createEvent = useCallback(async (eventData) => {
    try {
      const newEvent = await eventsApi.createEvent(ownerType, ownerId, eventData);

      // Enrich with pre-formatted display properties (UTC → local timezone conversion)
      const enrichedEvent = enrichEventWithDisplayProperties(newEvent);

      // A recurring create makes many events on the server but answers with
      // one. Appending that single row made a twelve-week series look like it
      // had created a single event — the rest only appeared on the next
      // refresh, which reads as the feature having failed. Series are fetched
      // back instead; single events keep the optimistic append, since there is
      // nothing else to go and get.
      if (newEvent?.recurrenceRuleId) {
        await fetchEvents(...lastFetchArgs.current);
        return { success: true, data: enrichedEvent };
      }

      setEvents(prev => [...prev, enrichedEvent]);
      return { success: true, data: enrichedEvent };
    } catch (err) {
      console.error('Error creating event:', err);
      return { success: false, error: err.message };
    }
  }, [ownerType, ownerId, fetchEvents]);

  const updateEventResponse = useCallback(async (eventId, status, userId = null, arrivalTime = null, applyToSeries = false) => {
    try {
      const result = await eventsApi.updateEventResponse(eventId, status, userId, arrivalTime, applyToSeries);
      const currentUser = getCurrentUser();

      // The target user ID is either the provided userId or the result's userId
      const targetUserId = result.userId;

      // Update the event's responses in state
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          // Check if this user already has a response
          const existingResponseIndex = event.responses.findIndex(r => r.userId === targetUserId);
          const newResponses = [...event.responses];

          // Get the username from the result or construct it
          const username = result.user?.first_name && result.user?.last_name
            ? `${result.user.first_name} ${result.user.last_name}`
            : (targetUserId === currentUser.id && currentUser.firstName && currentUser.lastName
              ? `${currentUser.firstName} ${currentUser.lastName}`
              : `User ${targetUserId}`);

          const newResponse = {
            userId: targetUserId,
            username: username,
            status: result.status,
            arrivalTime: result.arrivalTime || null
          };

          if (existingResponseIndex >= 0) {
            // Update existing response
            newResponses[existingResponseIndex] = newResponse;
          } else {
            // Add new response
            newResponses.push(newResponse);
          }

          return { ...event, responses: newResponses };
        }
        return event;
      }));

      return { success: true };
    } catch (err) {
      console.error('Error updating event response:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const getEventResponses = useCallback(async (eventId) => {
    try {
      return await eventsApi.getEventResponses(eventId);
    } catch (err) {
      console.error('Error fetching event responses:', err);
      return [];
    }
  }, []);

  const updateEventStatus = useCallback(async (eventId, status) => {
    try {
      const result = await eventsApi.updateEventStatus(eventId, status);

      // Update the event status in state
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          return { ...event, status: result.status || status };
        }
        return event;
      }));

      return { success: true, data: result };
    } catch (err) {
      console.error('Error updating event status:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const updateEvent = useCallback(async (eventId, eventData, scope = 'this') => {
    try {
      const transformed = await eventsApi.updateEvent(eventId, eventData, scope);

      // The merged row is handed back as well as stored, so a caller holding
      // its own copy of the event (the details modal) can adopt exactly what
      // the list now shows. Returning the raw API event instead is not
      // equivalent: it carries the server's `responses` shape rather than the
      // list's, so a caller spreading it would corrupt attendance.
      //
      // Built from the ref rather than inside the setEvents updater: React
      // runs that updater lazily, so anything it assigns is still unset by the
      // time this function returns.
      const existing = eventsRef.current.find(event => event.id === eventId);
      const merged = enrichEventWithDisplayProperties({
        ...existing,
        // Not merged from `existing` — it describes this call, not the event.
        reshaped: transformed.reshaped ?? null,
        subject: transformed.subject || existing?.subject,
        venue: transformed.venue || existing?.venue,
        start_time: transformed.start_time || existing?.start_time, // ISO timestamp (UTC)
        end_time: transformed.end_time || existing?.end_time, // ISO timestamp (UTC)
        // `??` not `||`: clearing capacity sends null and clearing courts sends
        // [], both of which `||` would discard in favour of the stale value.
        capacity: transformed.capacity ?? null,
        courtIds: transformed.courtIds ?? existing?.courtIds ?? []
      });

      setEvents(prev => prev.map(event => (event.id === eventId ? merged : event)));

      return { success: true, data: merged };
    } catch (err) {
      console.error('Error updating event:', err);
      return { success: false, error: err.message };
    }
  }, []);

  const deleteEvent = useCallback(async (eventId, scope = 'this') => {
    try {
      await eventsApi.deleteEvent(eventId, scope);

      // Remove the event from state (for series deletes, filter by recurrence rule or just the one)
      setEvents(prev => prev.filter(event => event.id !== eventId));

      return { success: true };
    } catch (err) {
      console.error('Error deleting event:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Load events when owner changes or component mounts.
  // NOTE: We intentionally do NOT clear events to [] before fetching — that
  // intermediate empty state causes downstream components (EventInfo) to briefly
  // see no events, which in turn notifies parents of a null event and triggers
  // "close scoring" / flicker cascades. The stale events stay visible until the
  // fresh fetch completes, which is a better UX anyway.
  useEffect(() => {
    setError(null);
    if (shouldLoad && ownerType && ownerId) {
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad, ownerType, ownerId]);

  return {
    events,
    loading,
    error,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    updateEventResponse,
    updateEventStatus,
    getEventResponses,
    refreshEvents: fetchEvents,
    // Refetch exactly what is on screen. Calling fetchEvents() bare would
    // silently widen or narrow the range, which is what lastFetchArgs exists
    // to prevent.
    refetchCurrent: () => fetchEvents(...lastFetchArgs.current)
  };
};
